/* global globalThis */
// Keep preview advice out of fingerprint-bound Column definitions.
const COLUMN_FIELDS = [
  "column_key", "display_name", "logical_type", "nullable", "unit",
  "decimal_precision", "decimal_scale",
];
const DECIMAL_FIELDS = ["decimal_precision", "decimal_scale"];

export const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
export const copyJson = (value) => JSON.parse(JSON.stringify(value));

export function positiveInteger(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !/^\d+$/.test(value)) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

export function columnKey(column) {
  return column.column_key;
}

export function snapshotColumns(columns) {
  if (!Array.isArray(columns) || !columns.length) throw new Error("Preview has no column definitions.");
  // Preserve suggestion order; an ordinal is display metadata, not a Column request field.
  const result = columns.map((column) => {
    if (!isObject(column)) throw new Error("Invalid column definition.");
    const clean = {};
    COLUMN_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(column, field) &&
          !(DECIMAL_FIELDS.includes(field) && column[field] == null)) {
        clean[field] = copyJson(column[field]);
      }
    });
    if (clean.nullable === undefined) clean.nullable = true;
    if (typeof columnKey(clean) !== "string" || !columnKey(clean).trim() ||
      typeof clean.display_name !== "string" || !clean.display_name.trim() ||
        typeof clean.logical_type !== "string" || !clean.logical_type.trim() ||
        typeof clean.nullable !== "boolean") {
      throw new Error("Each column needs column_key, display_name, logical_type and boolean nullable.");
    }
    return clean;
  });
  const keys = result.map(columnKey);
  if (new Set(keys).size !== keys.length) throw new Error("Column keys must be unique.");
  return result;
}

export function parseJson(text, label, expected) {
  let value;
  try { value = JSON.parse(text); } catch { throw new Error(`${label} must be valid JSON.`); }
  if ((expected === "object" && !isObject(value)) || (expected === "array" && !Array.isArray(value))) {
    throw new Error(`${label} must be a JSON ${expected}.`);
  }
  return value;
}

export function validateImportOptions(options, columns = null) {
  if (!isObject(options)) throw new Error("Preview is missing effective import_options.");
  if (typeof options.delimiter !== "string" || Array.from(options.delimiter).length !== 1) {
    throw new Error("Delimiter must be exactly one character.");
  }
  if (!["utf-8", "utf-8-sig"].includes(options.encoding)) throw new Error("Encoding must be utf-8 or utf-8-sig.");
  if (!Array.isArray(options.null_values) || options.null_values.some((value) => typeof value !== "string")) {
    throw new Error("Null values must be a JSON array of strings.");
  }
  if (options.column_mapping !== undefined && !isObject(options.column_mapping)) throw new Error("Column mapping must be a JSON object.");
  if (options.column_mapping !== undefined) {
    const sources = Object.values(options.column_mapping);
    if (sources.some((value) => typeof value !== "string" || !value.length) || new Set(sources).size !== sources.length) {
      throw new Error("Each mapping value must identify a distinct source header.");
    }
  }
  if (columns && options.column_mapping !== undefined) {
    const keys = columns.map(columnKey);
    const mapping = options.column_mapping;
    if (!isObject(mapping) || Object.keys(mapping).length !== keys.length ||
        keys.some((key) => !Object.prototype.hasOwnProperty.call(mapping, key))) {
      throw new Error("Effective mapping must cover every schema key exactly, with no extra keys.");
    }
  }
  return options;
}

export function reviewPreview(preview, existingColumns = null) {
  if (!isObject(preview)) throw new Error("Server returned no preview response.");
  if (preview.profile_valid !== true) throw new Error("The file profile is invalid. Resolve the diagnostics and preview again.");
  if (typeof preview.preview_token !== "string" || !preview.preview_token.trim()) throw new Error("Preview token is missing.");
  const columns = existingColumns
    ? snapshotColumns(existingColumns)
    : snapshotColumns(preview.suggested_schema?.columns);
  validateImportOptions(preview.import_options, columns);
  return { columns, preview_token: preview.preview_token, import_options: copyJson(preview.import_options) };
}

export function cryptographicKey(cryptoProvider = globalThis.crypto) {
  if (typeof cryptoProvider?.randomUUID === "function") return cryptoProvider.randomUUID();
  if (typeof cryptoProvider?.getRandomValues !== "function") {
    throw new Error("Secure randomness is unavailable. Use a secure browser context to import.");
  }
  const bytes = cryptoProvider.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function deepFreeze(value) {
  if (value && typeof value === "object") {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

export function freezeConfirmation({ sourceFileId, entraId, title, preview, schemaVersionId, columns, newSchema }, key) {
  const source = positiveInteger(sourceFileId);
  if (!source || typeof entraId !== "string" || !entraId.trim()) throw new Error("A committed file and signed-in account are required.");
  if (typeof title !== "string" || !title.trim()) throw new Error("A dataset title is required.");
  const version = positiveInteger(schemaVersionId);
  if (Boolean(version) === Boolean(newSchema)) throw new Error("Choose exactly one immutable schema version or new schema.");
  const accepted = reviewPreview(preview, version ? columns : null);
  const body = {
    entra_id: entraId,
    source_file_id: source,
    title: title.trim(),
    preview_token: accepted.preview_token,
    import_options: accepted.import_options,
    idempotency_key: key || cryptographicKey(),
  };
  if (version) body.schema_version_id = version;
  else {
    if (typeof newSchema.name !== "string" || !newSchema.name.trim()) throw new Error("A new schema name is required.");
    if (newSchema.description !== undefined && typeof newSchema.description !== "string") {
      throw new Error("New schema description must be a string when supplied.");
    }
    body.new_schema = {
      name: newSchema.name.trim(),
      ...(newSchema.description === undefined ? {} : { description: newSchema.description }),
      is_reusable: newSchema.is_reusable === true,
      columns: accepted.columns,
    };
  }
  return deepFreeze(copyJson(body));
}

export function importErrorMessage(error) {
  const data = error?.response?.data;
  if (data !== undefined) return typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return error?.message || "Request failed.";
}

export function ambiguousImportError(error) {
  const status = error?.response?.status;
  return !status || status >= 500 || status === 408;
}